import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { apiClient } from '@/services/api';
import type { AdminUsersFilter } from '@/constants';
import type { AdminStats, AdminUserActionKey, AdminUsersPage } from '@/types/adminUsers';

const ADMIN_USERS_QUERY_KEY = ['admin', 'users'] as const;
const ADMIN_STATS_QUERY_KEY = ['admin', 'stats'] as const;

const USERS_PAGE_SIZE = 20;

interface AdminUsersMessage {
  type: 'success' | 'error';
  text: string;
}

interface UseAdminUsersParams {
  filter: AdminUsersFilter;
  page: number;
  search: string;
}

interface ActionResponse {
  success: boolean;
  /** Set by send-verification when the token was created but SMTP is off. */
  warning?: unknown;
}

const apiErrorMessage = (error: unknown): string | undefined =>
  (error as { response?: { data?: { message?: string } } })?.response?.data?.message;

/**
 * Admin users list + stats (React Query) and the per-user block / unblock /
 * send-verification actions. Several actions may run at once; each key stays
 * pending until its request finishes and the refetched list/stats arrive.
 */
export function useAdminUsers({ filter, page, search }: UseAdminUsersParams) {
  const { t } = useTranslation('admin');
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const [pendingActions, setPendingActions] = useState<ReadonlySet<AdminUserActionKey>>(
    () => new Set(),
  );
  const [message, setMessage] = useState<AdminUsersMessage | null>(null);

  const usersQuery = useQuery({
    queryKey: [...ADMIN_USERS_QUERY_KEY, filter, page, search],
    queryFn: async () => {
      const response = await apiClient.getAdminUsers({
        page,
        limit: USERS_PAGE_SIZE,
        filter,
        ...(search ? { search } : {}),
      });
      return response.data.data as AdminUsersPage;
    },
    enabled: isAuthenticated,
    placeholderData: keepPreviousData,
  });

  const statsQuery = useQuery({
    queryKey: ADMIN_STATS_QUERY_KEY,
    queryFn: async () => {
      const response = await apiClient.getAdminStats();
      return response.data.data as AdminStats;
    },
    enabled: isAuthenticated,
  });

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ADMIN_USERS_QUERY_KEY }),
      queryClient.invalidateQueries({ queryKey: ADMIN_STATS_QUERY_KEY }),
    ]);

  const setPending = (key: AdminUserActionKey, pending: boolean) =>
    setPendingActions((prev) => {
      const next = new Set(prev);
      if (pending) next.add(key);
      else next.delete(key);
      return next;
    });

  const runAction = async (
    key: AdminUserActionKey,
    request: () => Promise<{ data: ActionResponse }>,
    toMessage: (data: ActionResponse) => AdminUsersMessage,
    errorText: string,
  ) => {
    if (pendingActions.has(key)) return;
    setPending(key, true);
    try {
      const response = await request();
      if (response.data.success) {
        setMessage(toMessage(response.data));
        // Keep the button disabled until the refreshed row replaces the stale one.
        await invalidate();
      }
    } catch (error) {
      setMessage({ type: 'error', text: apiErrorMessage(error) || errorText });
    } finally {
      setPending(key, false);
    }
  };

  const blockUser = (userId: number) => {
    const reason = window.prompt(t('userBlockPrompt'));
    if (!reason) return;
    runAction(
      `block-${userId}`,
      () => apiClient.blockUser(userId, reason),
      () => ({ type: 'success', text: t('userBlockedSuccess') }),
      t('userBlockError'),
    );
  };

  const unblockUser = (userId: number) =>
    runAction(
      `unblock-${userId}`,
      () => apiClient.unblockUser(userId),
      () => ({ type: 'success', text: t('userUnblockedSuccess') }),
      t('userUnblockError'),
    );

  const sendVerification = (userId: number) =>
    runAction(
      `verify-${userId}`,
      () => apiClient.adminSendVerification(userId),
      (data) =>
        data.warning
          ? { type: 'error', text: t('verificationSmtpWarning') }
          : { type: 'success', text: t('verificationSent') },
      t('verificationSendError'),
    );

  const loadError = usersQuery.isError
    ? apiErrorMessage(usersQuery.error) || t('usersLoadError')
    : null;

  return {
    usersPage: usersQuery.data,
    isFirstLoad: usersQuery.isLoading,
    /** True while `usersPage` still shows the previous key's result. */
    isStaleData: usersQuery.isPlaceholderData,
    stats: statsQuery.data ?? null,
    loadError,
    message,
    dismissMessage: () => setMessage(null),
    pendingActions,
    blockUser,
    unblockUser,
    sendVerification,
  };
}
